import { NotificationCount } from "@/components/notification-count/NotificationCount.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { usePerformanceReviewsHook } from "@/hooks/use-performance-reviews.hook.tsx";
import useTimeOffHook from "@/hooks/use-time-off.hook.tsx";
import { HiringPlanRequestViewModal } from "@/pages/recruit/hiring-plan/widgets/HiringPlanRequestViewModal.tsx";
import { OfferRequestViewModal } from "@/pages/recruit/offers/widgets/OfferRequestViewModal.tsx";
import { WorkflowRequestViewModal } from "@/pages/workflows/widgets/WorkflowRequestViewModal.tsx";
import { EsignRequestRow } from "@/pages/esign/widgets/EsignRequestRow.tsx";
import { TaskRow } from "@/pages/tasks/components/TaskRow.tsx";
import { useEmployeeToday } from "@/queries/employees/use-employee-today.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import {
  ApiApprovalRequestFull,
  ApiApprovalRequestType,
} from "@/types/approvals/approval.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { ApiTodayItem, ApiTodayItemType } from "@/types/employees/employee-today.types.ts";
import { getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import { ApiSurveyType } from "@/types/survey.types.ts";
import { ApiTimeOffPeriodTrackingTime } from "@/types/time-off/time-off-policy.types.ts";
import {
  Avatar,
  Box,
  Button,
  DotSeparator,
  FontAwesomeIcon,
  ListItem,
  Stack,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { differenceInCalendarDays } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export const HomeThingsToDo = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: todayResult } = useEmployeeToday();
  const { data: employeeMe } = useEmployeeMe();
  const { renderEmployeeName, renderDate } = useFormatter();
  const { openModal } = useModals();
  const { openViewRequest } = useTimeOffHook();
  const { openSelectPeers } = usePerformanceReviewsHook();

  const ITEMS_LIMIT = 5;
  const [showAllOverdue, setShowAllOverdue] = useState(false);
  const [showAllItems, setShowAllItems] = useState(false);

  const processItems = (items: ApiTodayItem[]): ApiTodayItem[] => {
    const performanceReviews: ApiTodayItem[] = [];
    const surveys: ApiTodayItem[] = [];
    const isGroupableSurvey = (item: ApiTodayItem) =>
      item.type === ApiTodayItemType.survey &&
      item.survey?.type !== ApiSurveyType.survey_with_approval;

    for (const item of items) {
      if (item.type === ApiTodayItemType.performance_review) {
        performanceReviews.push(item);
      } else if (isGroupableSurvey(item)) {
        surveys.push(item);
      }
    }

    const result: ApiTodayItem[] = [];
    let prInserted = false;
    let surveyInserted = false;

    for (const item of items) {
      if (item.type === ApiTodayItemType.performance_review) {
        if (!prInserted) {
          prInserted = true;
          if (performanceReviews.length > 1) {
            result.push({
              type: ApiTodayItemType.grouped_performance_reviews,
              subitems: performanceReviews,
            } as ApiTodayItem);
          } else {
            result.push(item);
          }
        }
      } else if (isGroupableSurvey(item)) {
        if (!surveyInserted) {
          surveyInserted = true;
          if (surveys.length > 1) {
            result.push({
              type: ApiTodayItemType.grouped_surveys,
              subitems: surveys,
            } as ApiTodayItem);
          } else {
            result.push(item);
          }
        }
      } else {
        result.push(item);
      }
    }

    return result;
  };

  const processedOverdueItems = useMemo(
    () => processItems(todayResult?.overdue_items ?? []),
    [todayResult?.overdue_items]
  );
  const processedItems = useMemo(
    () => processItems(todayResult?.items ?? []),
    [todayResult?.items]
  );

  const hasOverdueItems = processedOverdueItems.length > 0;
  const hasItems = processedItems.length > 0;

  if (!todayResult || (!hasOverdueItems && !hasItems)) {
    return null;
  }

  const visibleOverdue = showAllOverdue
    ? processedOverdueItems
    : processedOverdueItems.slice(0, ITEMS_LIMIT);
  const visibleItems = showAllItems ? processedItems : processedItems.slice(0, ITEMS_LIMIT);
  const hiddenOverdueCount = processedOverdueItems.length - ITEMS_LIMIT;
  const hiddenItemsCount = processedItems.length - ITEMS_LIMIT;

  const renderPerformanceReviewInvite = (item: ApiTodayItem) => {
    const performanceReviewInvite = item.performance_review_invite;
    if (!performanceReviewInvite || !employeeMe) return null;
    return (
      <ListItem
        title={t("performance_review:you_been_invited_to_select_peers")}
        forceShowAvatar={true}
        icon={<Avatar icon={<FontAwesomeIcon icon={"fa-light fa-gauge-high"} />} />}
        captionSlot={
          <DotSeparator>
            <Text className={"text-secondary"}>{performanceReviewInvite.review.title}</Text>
          </DotSeparator>
        }
        valueSlot={
          <Button
            rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-right" />}
            variant="plain"
            onClick={() =>
              openSelectPeers({
                performanceReviewId: performanceReviewInvite.review.id,
                employeeId: employeeMe.id,
                reviewerTypes: performanceReviewInvite.reviewer_types,
                withStatuses: false,
              })
            }
          >
            {t("performance_review:select_peers")}
          </Button>
        }
        behavior={"card"}
      />
    );
  };

  const renderPerformanceReview = (item: ApiTodayItem) => {
    const performanceReview = item.performance_review;
    if (!performanceReview) return null;
    return (
      <ListItem
        title={
          performanceReview.participant.id === employeeMe?.id
            ? t("performance_review:leave_feedback_about_your_work")
            : t("performance_review:leave_performance_feedback")
        }
        icon={
          !getEmployeeAvatarUrl(performanceReview.participant, "lg") && (
            <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-gauge-high"} />} />
          )
        }
        forceShowAvatar={true}
        avatarUrl={getEmployeeAvatarUrl(performanceReview.participant, "lg")}
        captionSlot={
          <DotSeparator>
            <Text className={"text-secondary"}>
              {renderEmployeeName(performanceReview.participant)}
            </Text>

            <Text className={"text-secondary"}>
              {getEmployeePositionTitle(performanceReview.participant) || "-"}
            </Text>

            <Text className={"text-secondary"}>{performanceReview.title}</Text>
          </DotSeparator>
        }
        valueSlot={
          <Button
            rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-right" />}
            variant="plain"
            onClick={async () => {
              navigate(`/performance-reviews/reviewers/${performanceReview.reviewer_id}/respond`);
            }}
          >
            {t("common:leave_feedback")}
          </Button>
        }
        behavior={"card"}
      />
    );
  };

  const renderSurvey = (item: ApiTodayItem) => {
    const survey = item.survey;
    if (!survey) return null;
    const isRequest = survey.type === ApiSurveyType.survey_with_approval;
    return (
      <ListItem
        title={
          isRequest ? t("surveys:request_notification_title") : t("surveys:app_notification_title")
        }
        emoji={survey.emoji}
        forceShowAvatar={true}
        captionSlot={
          <DotSeparator>
            <Text className={"text-secondary"}>{survey.title}</Text>
            {!isRequest && (
              <Text className={"text-secondary"}>
                {survey.is_anonymous ? t("common:anonymous") : t("common:non_anonymous")}
              </Text>
            )}
          </DotSeparator>
        }
        valueSlot={
          <Button
            rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-right" />}
            variant={"plain"}
            onClick={async () => {
              navigate(`/surveys/${survey.id}/respond`);
            }}
          >
            {isRequest ? t("surveys:request_fill") : t("surveys:start_survey")}
          </Button>
        }
        behavior={"card"}
      />
    );
  };

  const renderFeedback = (item: ApiTodayItem) => {
    const feedback = item.feedback;
    if (!feedback) return null;
    return (
      <ListItem
        title={t("scorecards:app_notification_title")}
        forceShowAvatar={true}
        avatarUrl={feedback.applicant?.photo_url}
        captionSlot={
          <DotSeparator>
            {feedback.job?.title && <Text className={"text-secondary"}>{feedback.job?.title}</Text>}
            <Text
              className={"text-secondary"}
            >{`${feedback.applicant?.first_name ?? ""} ${feedback.applicant?.last_name ?? ""}`}</Text>
          </DotSeparator>
        }
        valueSlot={
          <Button
            rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-right" />}
            variant="plain"
            onClick={async () => {
              navigate(`/feedbacks/${feedback.id}/candidates/${feedback.candidate_id}`);
            }}
          >
            {t("common:leave_feedback")}
          </Button>
        }
        behavior={"card"}
      />
    );
  };

  const renderTimeOffRequest = (item: ApiTodayItem) => {
    const timeOffLog = item.time_off_log;
    if (!timeOffLog || !timeOffLog.approval_request_details) return null;

    const isHours =
      timeOffLog.time_off_log.category?.period_track_type === ApiTimeOffPeriodTrackingTime.hours;
    const pluralKey = isHours ? "plurals:plural_hours" : "plurals:plural_day";
    const daysCount =
      timeOffLog.time_off_log?.date_from && timeOffLog.time_off_log.date_to
        ? differenceInCalendarDays(
            timeOffLog.time_off_log.date_to,
            timeOffLog.time_off_log.date_from
          ) + 1
        : 0;
    const duration =
      t(pluralKey, {
        count: -parseFloat(timeOffLog.time_off_log.balance),
      }) +
      (isHours
        ? ` (${t("time_off:in_units")} ${t("plurals:plural_day", {
            count: daysCount,
          })})`
        : "");

    return (
      <ListItem
        title={`${renderEmployeeName(timeOffLog.approval_request_details.employee)} ${t("time_off:employee_requests")} ${timeOffLog.time_off_log.category.title}`}
        avatarUrl={getEmployeeAvatarUrl(timeOffLog.approval_request_details.employee, "lg")}
        forceShowAvatar={true}
        captionSlot={
          <DotSeparator>
            {timeOffLog.time_off_log.date_from && timeOffLog.time_off_log.date_to && (
              <Text
                className={"text-secondary"}
              >{`${renderDate(timeOffLog.time_off_log.date_from)} — ${renderDate(timeOffLog.time_off_log.date_to)}`}</Text>
            )}
            <Text className={"text-secondary"}>{duration}</Text>
          </DotSeparator>
        }
        valueSlot={
          <Button
            variant="plain"
            rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-right" />}
            onClick={async () => {
              openViewRequest(timeOffLog.approval_request_details!.id);
            }}
          >
            {t("approval:review")}
          </Button>
        }
        behavior={"card"}
      />
    );
  };

  const renderOfferRequest = (item: ApiTodayItem) => {
    const offerRequest = item.offer_request;
    if (!offerRequest || !offerRequest.approval_request_details) return null;

    return (
      <ListItem
        title={t("approval:new_offer_request_awaiting_approval")}
        icon={
          <Avatar
            icon={<FontAwesomeIcon icon={"fa-light fa-file-contract"} className={"text-[22px]"} />}
          />
        }
        forceShowAvatar={true}
        captionSlot={
          <DotSeparator>
            <Text
              className={"text-secondary"}
            >{`${offerRequest.offer_data.first_name} ${offerRequest.offer_data.last_name}`}</Text>
            <Text className={"text-secondary"}>{offerRequest.offer_data?.job_title}</Text>
          </DotSeparator>
        }
        valueSlot={
          <Button
            rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-right" />}
            variant="plain"
            onClick={async () => {
              openModal(OfferRequestViewModal, {
                requestId: offerRequest.approval_request_details?.id,
              });
            }}
          >
            {t("approval:review")}
          </Button>
        }
        behavior={"card"}
      />
    );
  };

  const renderHiringPlanRequest = (item: ApiTodayItem) => {
    const hiringPlanRequest = item.hiring_plan;
    if (!hiringPlanRequest || !hiringPlanRequest.approval_request_details) return null;

    return (
      <ListItem
        title={t("approval:new_hiring_plan_request_awaiting_approval")}
        icon={
          <Avatar
            icon={
              <FontAwesomeIcon
                icon={"fa-light fa-user-magnifying-glass"}
                className={"text-[22px]"}
              />
            }
          />
        }
        avatarUrl={getEmployeeAvatarUrl(hiringPlanRequest.approval_request_details.employee, "lg")}
        forceShowAvatar={true}
        captionSlot={
          <DotSeparator>
            <Text className={"text-secondary"}>{hiringPlanRequest.hiring_plan_data.title}</Text>
            <Text className={"text-secondary"}>
              {renderEmployeeName(hiringPlanRequest.approval_request_details.employee)}
            </Text>
          </DotSeparator>
        }
        valueSlot={
          <Button
            rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-right" />}
            variant="plain"
            onClick={async () => {
              openModal(HiringPlanRequestViewModal, {
                requestId: hiringPlanRequest.approval_request_details?.id,
              });
            }}
          >
            {t("approval:review")}
          </Button>
        }
        behavior={"card"}
      />
    );
  };

  const renderWorkflowApprovalRequest = (approvalRequest: ApiApprovalRequestFull) => {
    if (!approvalRequest.approval_request_details) return null;
    const employee = approvalRequest.approval_request_details.employee;
    const processTitle =
      approvalRequest.workflow_data?.workflow.title || t("approval:approval_process");

    return (
      <ListItem
        title={t("approval:new_approval_request")}
        icon={
          <Avatar
            icon={<FontAwesomeIcon icon="fa-light fa-check-double" className={"text-[22px]"} />}
          />
        }
        forceShowAvatar={true}
        captionSlot={
          <Text className={"text-secondary"}>
            {t("approval:approval_request_changes_caption", {
              employee: renderEmployeeName(employee),
              process: processTitle,
            })}
          </Text>
        }
        valueSlot={
          <Button
            rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-right" />}
            variant="plain"
            onClick={async () => {
              openModal(WorkflowRequestViewModal, {
                requestId: approvalRequest.approval_request_details?.id,
              });
            }}
          >
            {t("approval:review")}
          </Button>
        }
        behavior={"card"}
      />
    );
  };

  const renderApprovalRequest = (item: ApiTodayItem) => {
    const approvalRequest = item.approval_request;
    if (!approvalRequest || !approvalRequest.approval_request_details) return null;

    switch (approvalRequest.approval_request_details.type) {
      case ApiApprovalRequestType.workflow:
        return renderWorkflowApprovalRequest(approvalRequest);
      default:
        return null;
    }
  };

  const renderEsignRequest = (item: ApiTodayItem) => {
    if (!item.esign_request) return null;
    return <EsignRequestRow esignRequest={item.esign_request} />;
  };

  const renderGroupedPerformanceReviews = (item: ApiTodayItem) => {
    const count = item.subitems?.length ?? 0;
    return (
      <ListItem
        title={t("performance_review:leave_performance_feedback")}
        forceShowAvatar={true}
        icon={<Avatar icon={<FontAwesomeIcon icon={"fa-light fa-gauge-high"} />} />}
        captionSlot={
          <Text className={"text-secondary"}>
            {t("plurals:feedback_requests_assigned_to_you", { count })}
          </Text>
        }
        valueSlot={
          <Button
            rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-right" />}
            variant="plain"
            onClick={() => navigate("/my-feedback/requests")}
          >
            {t("common:review_all")}
          </Button>
        }
        behavior={"card"}
      />
    );
  };

  const renderGroupedSurveys = (item: ApiTodayItem) => {
    const count = item.subitems?.length ?? 0;
    return (
      <ListItem
        title={t("surveys:surveys")}
        forceShowAvatar={true}
        icon={<Avatar icon={<FontAwesomeIcon icon={"fa-light fa-poll-people"} />} />}
        captionSlot={
          <Text className={"text-secondary"}>
            {t("plurals:surveys_awaiting_your_response", { count })}
          </Text>
        }
        valueSlot={
          <Button
            rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-right" />}
            variant="plain"
            onClick={() => navigate("/surveys/requests")}
          >
            {t("common:view_all")}
          </Button>
        }
        behavior={"card"}
      />
    );
  };

  const renderItem = (item: ApiTodayItem) => {
    switch (item.type) {
      case ApiTodayItemType.grouped_performance_reviews:
        return renderGroupedPerformanceReviews(item);
      case ApiTodayItemType.grouped_surveys:
        return renderGroupedSurveys(item);
      case ApiTodayItemType.survey:
        return renderSurvey(item);
      case ApiTodayItemType.time_off_log:
        return renderTimeOffRequest(item);
      case ApiTodayItemType.offer_request:
        return renderOfferRequest(item);
      case ApiTodayItemType.performance_review:
        return renderPerformanceReview(item);
      case ApiTodayItemType.performance_review_invite:
        return renderPerformanceReviewInvite(item);
      case ApiTodayItemType.feedback:
        return renderFeedback(item);
      case ApiTodayItemType.hiring_plan:
        return renderHiringPlanRequest(item);
      case ApiTodayItemType.approval_request:
        return renderApprovalRequest(item);
      case ApiTodayItemType.task:
        return <TaskRow task={item.task!} />;
      case ApiTodayItemType.esign_request:
        return renderEsignRequest(item);
    }

    return null;
  };

  return (
    <Stack>
      <Stack>
        <Title
          header={t("tasks:things_to_do")}
          size={"lg"}
          paddingBottom
          paddingTop
          actionIcon={<FontAwesomeIcon icon={"fa-solid fa-chevron-right"} />}
          to={"/tasks"}
        />
        {hasOverdueItems && (
          <Box>
            <Title
              header={t("tasks:section_title_overdue")}
              size={"xs"}
              paddingBottom
              className={"text-warning"}
            />
            <Stack gap={"sm"}>
              {visibleOverdue.map((item, i) => (
                <Box
                  className={"rounded-xs border-brand-dark/5 px-6 py-4 bg-light"}
                  key={`today-overdue-${i}`}
                >
                  {renderItem(item)}
                </Box>
              ))}
            </Stack>
            {!showAllOverdue && hiddenOverdueCount > 0 && (
              <Button
                variant="secondary"
                onClick={() => setShowAllOverdue(true)}
                rightIcon={<NotificationCount count={hiddenOverdueCount} size={"sm"} />}
                className={"mt-4"}
              >
                {t("tasks:more_tasks_for_you")}
              </Button>
            )}
          </Box>
        )}
      </Stack>
      {hasItems && (
        <Box>
          {hasOverdueItems && (
            <Title header={t("tasks:section_title_todo")} size={"xs"} paddingTop paddingBottom />
          )}
          <Stack gap={"sm"}>
            {visibleItems.map((item, i) => (
              <Box
                className={"rounded-xs border-brand-dark/5 px-6 py-4 bg-light"}
                key={`today-item-${i}`}
              >
                {renderItem(item)}
              </Box>
            ))}
          </Stack>
          {!showAllItems && hiddenItemsCount > 0 && (
            <Button
              variant="secondary"
              onClick={() => setShowAllItems(true)}
              rightIcon={<NotificationCount count={hiddenItemsCount} size={"sm"} />}
              className={"mt-4"}
            >
              {t("tasks:more_tasks_for_you")}
            </Button>
          )}
        </Box>
      )}
    </Stack>
  );
};
